import { SummaryViewStepStatus } from '@areapif/areapif-shared'

import type { HistoricalInspection } from '../models/inspection'
import { convertToInspection } from '../models/inspection'
import { buildEvidenceSteps, InspectionState } from './activeInspection'
import type { TranslateFn } from './activeInspectionCells'
import { formatDay } from './activeInspectionCells'
import type { ActiveInspectionExport } from './activeInspectionExport'

/**
 * The columns of the Inspecciones export, in the order they are written. The
 * file is read to count complete and incomplete inspections, so it carries one
 * row per inspection and only what that count is sliced by — no evidence.
 */
export const HISTORICAL_INSPECTION_EXPORT_COLUMNS = [
  'dayOfMonth',
  'day',
  'container',
  'inspectionLevel',
  'location',
  'completeness',
] as const

export type HistoricalInspectionExportColumn = (typeof HISTORICAL_INSPECTION_EXPORT_COLUMNS)[number]

/**
 * Translation key of each column's header. The columns the table also shows
 * reuse the table's own header; the day of the month and the completeness
 * verdict, which the table has no column for, get one of their own under
 * `export`.
 */
const HEADER_KEYS: Record<HistoricalInspectionExportColumn, string> = {
  dayOfMonth: 'resources.historical_inspections.export.fields.day_of_month',
  day: 'resources.historical_inspections.fields.day',
  container: 'resources.historical_inspections.fields.container',
  inspectionLevel: 'resources.historical_inspections.fields.inspection_level',
  location: 'resources.historical_inspections.fields.location',
  completeness: 'resources.historical_inspections.export.fields.completeness',
}

/**
 * Whether every step of the inspection's evidence strip has evidence.
 *
 * Read off the same strip the table's Evidencias column renders, built the same
 * way, so the file and the screen agree on every row: an archived inspection is
 * FINISHED, which leaves each step either with evidence or without it.
 *
 * A level whose flow asks for no evidence (DT, ES, FU, PR, TD: an empty strip)
 * is complete — nothing it required is missing.
 */
export const isInspectionComplete = (record: HistoricalInspection): boolean =>
  buildEvidenceSteps(convertToInspection(record).summaryView, InspectionState.FINISHED).every(
    step => step.status === SummaryViewStepStatus.DONE_COMPLETE,
  )

/**
 * Día: the day of the month of the date Fecha shows, parsed the same way so the
 * two columns cannot name different days. Empty when there is no date or it
 * does not parse.
 */
const formatDayOfMonth = (day: string | null): string => {
  if (!day) return ''
  const date = new Date(day)
  return Number.isNaN(date.getTime()) ? '' : String(date.getDate())
}

const cellOf = (translate: TranslateFn, record: HistoricalInspection, column: HistoricalInspectionExportColumn): string => {
  switch (column) {
    case 'dayOfMonth':
      return formatDayOfMonth(record.day)
    case 'day':
      return formatDay(record.day)
    case 'container':
      return record.container
    case 'inspectionLevel':
      // The stored code, not the chip's label: it is the same in every
      // language, which is what a spreadsheet counting by level needs.
      return record.inspection_level
    case 'location':
      return translate(`resources.historical_inspections.enums.location.${record.location}`)
    case 'completeness':
      // The literals spreadsheets read as a boolean (and can show as a
      // checkbox), untranslated so a count does not depend on the language.
      return isInspectionComplete(record) ? 'TRUE' : 'FALSE'
  }
}

/** The file's content: the translated headers and one row per inspection, in the order given. */
export const buildHistoricalInspectionExport = (records: HistoricalInspection[], translate: TranslateFn): ActiveInspectionExport => ({
  headers: HISTORICAL_INSPECTION_EXPORT_COLUMNS.map(column => translate(HEADER_KEYS[column])),
  rows: records.map(record => HISTORICAL_INSPECTION_EXPORT_COLUMNS.map(column => cellOf(translate, record, column))),
})
